import { Button, Card, Input, Modal, Tag } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "@/shared/hooks/usePermission";
import { useAcknowledgeEmployeeRisk } from "../hooks/useEmployeeProfile";
import type { EmployeeDetail } from "../types";

/** Sabab maydonining eng qisqa uzunligi — server ham `min(3)` talab qiladi. */
const MIN_REASON = 3;

/**
 * Bayroq rangi — davomat palitrasidagi qizil.
 *
 * ⚠️ Yashil UMUMAN ishlatilmaydi (yashil↔qizil deuteranopiyada ΔE 4,1,
 * chegara 8). «Ko'rib chiqilgan» holati ko'k/neytral bilan beriladi.
 */
const XAVF_RANGI = "#d03b3b";

/**
 * «Xavf balli» yon kartasi (kanvas, o'ng ustun).
 *
 * ⚠️⚠️ **«100 balldan» maxraji CHIZILMAYDI** — kanvasda u bor, lekin u
 * YOLG'ON: `computeRiskScore` (`packages/domain/src/risk.ts`) chegarasiz
 * yig'indi (`RISK_WEIGHTS`: low=1 · medium=3 · high=9), ya'ni 100
 * shkalasi UMUMAN yo'q. Standart chegara — 10
 * (`packages/core/src/settings.ts`). 86 ni «100 dan» ko'rsatish uni
 * «deyarli maksimal» qilib ko'rsatardi, aslida u chegaradan 8,6 barobar
 * yuqori.
 *
 * ⚠️⚠️ **Kanvasdagi ball tarkibi («+34 fake location…») ham
 * CHIZILMAYDI** — `suspicion_events` uchun o'qish marshruti hali yo'q va
 * uni bu bosqichda qo'shish orkestrator qarori bilan chiqarilgan.
 * Soxta tarkib esa auditga yaroqsiz raqamlar berardi.
 *
 * ⚠️ **«Ko'rilmagan» = `riskFlag && riskAcknowledgedAt === null`.**
 * Bayroqning O'ZI hisoblanadi va qayta hisoblashdan keyin QAYTISHI
 * mumkin, ya'ni «bayroq bor, lekin ko'rilgan» — HAQIQIY holat va unga
 * alohida matn kerak.
 */
export function RiskCard({ employee }: { employee: EmployeeDetail }) {
	const { t } = useTranslation("employees");
	const { can } = usePermission();
	const [ochiq, setOchiq] = useState(false);
	const [sabab, setSabab] = useState("");
	const acknowledge = useAcknowledgeEmployeeRisk(employee.id);

	const korilmagan = employee.riskFlag && employee.riskAcknowledgedAt === null;
	const belgilashMumkin = can("employees", "risk_acknowledge") && korilmagan;

	const yopish = () => {
		setOchiq(false);
		setSabab("");
	};

	return (
		<Card
			title={t("riskCard.title")}
			extra={
				employee.riskFlag ? (
					<Tag color="red">{korilmagan ? t("riskCard.notSeen") : t("riskCard.seen")}</Tag>
				) : null
			}
		>
			<div className="flex items-baseline gap-2">
				<span
					className="text-3xl font-semibold leading-none"
					style={{ color: employee.riskFlag ? XAVF_RANGI : undefined }}
				>
					{employee.riskScore}
				</span>

				{employee.riskComputedAt ? (
					<span className="text-xs text-[#8a929e]">
						{t("riskCard.computedAt", {
							vaqt: dayjs(employee.riskComputedAt).format("DD.MM HH:mm"),
						})}
					</span>
				) : (
					<span className="text-xs text-[#8a929e]">{t("riskCard.notComputedYet")}</span>
				)}
			</div>

			{employee.riskFlag ? (
				<div className="mt-2">
					<Tag color="red">{t("riskCard.overThreshold")}</Tag>
				</div>
			) : null}

			{employee.riskFlag && !korilmagan && employee.riskAcknowledgedAt ? (
				<div className="mt-2 text-xs text-[#8a929e]">
					{t("riskCard.seenAt", {
						sana: dayjs(employee.riskAcknowledgedAt).format("DD.MM.YYYY"),
					})}
				</div>
			) : null}

			{belgilashMumkin ? (
				<Button className="mt-3 w-full" onClick={() => setOchiq(true)}>
					{t("riskCard.acknowledgeButton")}
				</Button>
			) : null}

			<Modal
				open={ochiq}
				title={t("riskCard.modalTitle")}
				okText={t("riskCard.modalOk")}
				cancelText={t("riskCard.modalCancel")}
				confirmLoading={acknowledge.isPending}
				// ⚠️ Server `reason` uchun `min(3)` talab qiladi — bo'sh sabab
				// bilan yuborilgan so'rov 422 bilan qaytardi.
				okButtonProps={{ disabled: sabab.trim().length < MIN_REASON }}
				onOk={() => acknowledge.mutate(sabab.trim(), { onSuccess: yopish })}
				onCancel={yopish}
				destroyOnHidden
			>
				<Input.TextArea
					rows={3}
					value={sabab}
					onChange={(e) => setSabab(e.target.value)}
					placeholder={t("riskCard.reasonPlaceholder")}
				/>
			</Modal>
		</Card>
	);
}
